import moment from 'moment'

import config from '@/config'
import { RECEIPT_PAYMENT_TYPES } from '@/modules/base/util/constants'
import { TAG_LABELS } from '@/modules/file/constants'
import type { Booking } from '@/modules/receipt/types'

export interface ReviewBooking extends Booking {
  reviewPaymentId?: string
}

// FLAG:useDocuAi — the „Art" of a reviewable document, as a system-tag LABEL. Exactly what
// getDocumentInvoiceKindLabel returns, and the one tag the review must never drop: it is what makes
// the document a reviewable incoming/cancellation invoice in the first place.
const REVIEW_ART_TAG_LABELS: string[] = [TAG_LABELS.INCOMING_INVOICE, TAG_LABELS.CANCELLATION]

interface ReviewSaveArgs {
  updateFileInfo: (data: Record<string, unknown>) => Promise<unknown>
  fileId: string | number
  // What the reviewer has in the FORM's tag field.
  tags: unknown[]
  // FLAG:useDocuAi — the host's file-derived tag list (buildDocumentInvoiceReviewTags). Read for one
  // reason only: to recover the „Art" system tag the form must never drop. See mergeReviewTags.
  hostTags?: unknown[]
  documentInvoiceDetails: Record<string, unknown>
  createExpenseFromReview: boolean
  reReviewSave: boolean
  bookings?: ReviewBooking[]
}

const normalizeBookingDate = (bookingDate?: string) => {
  if (!bookingDate) return bookingDate
  const apiDate = moment(bookingDate, config.dateFormat.client).format(config.dateFormat.api)
  return moment(apiDate).isValid() ? apiDate : bookingDate
}

export const buildReviewBookingAssignments = (bookings: ReviewBooking[] = []) => bookings
  .filter(booking => booking.isNew)
  .map(booking => {
    if (booking.bookingType === RECEIPT_PAYMENT_TYPES.BANK_TRANSFER) {
      throw new Error('A document review save cannot start a bank transfer')
    }
    if (![RECEIPT_PAYMENT_TYPES.BOOKING, RECEIPT_PAYMENT_TYPES.ASSIGNMENT].includes(booking.bookingType)) {
      throw new Error('Unsupported document review payment type')
    }
    if (!booking.reviewPaymentId) {
      throw new Error('Document review payment is missing its retry id')
    }
    return {
      ...booking,
      bookingDate: normalizeBookingDate(booking.bookingDate),
    }
  })

// FLAG:useDocuAi — which tags the review actually saves. Until now the save sent the tags the HOST
// had computed from the file when the review was opened, so everything the reviewer did in the
// form's tag field was silently thrown away. The form wins now — with the „Art" system tag added
// back, because that tag is the document's classification, not a label the reviewer picked.
// Order: „Art" first (same as the host list it replaces), then the reviewer's own tags, deduped.
export const mergeReviewTags = (formTags?: unknown[], hostTags: unknown[] = []) => {
  // No tag field in this form at all (a host whose data was not built by build-review-data): keep
  // the host's list verbatim rather than reading „nothing selected" into a missing answer.
  if (!Array.isArray(formTags)) { return [...(hostTags ?? [])] }

  const artTags = (hostTags ?? []).filter(tag => typeof tag === 'string' && REVIEW_ART_TAG_LABELS.includes(tag))
  const merged: unknown[] = []
  for (const tag of [...artTags, ...(formTags ?? [])]) {
    if (tag === null || tag === undefined || tag === '') { continue }
    if (!merged.includes(tag)) { merged.push(tag) }
  }
  return merged
}

// FLAG:useDocuAi — one shared save boundary for every invoice-review host. Explicit payments
// travel in the SAME request as the reviewed document, so the API can create/sync the target
// receipt first and apply the payment transactionally. This never invokes the bank-transfer
// endpoint; real transfers are deliberately rejected above.
export const saveDocumentInvoiceReview = async({
  updateFileInfo,
  fileId,
  tags,
  hostTags,
  documentInvoiceDetails,
  createExpenseFromReview,
  reReviewSave,
  bookings = [],
}: ReviewSaveArgs) => {
  const bookingAssignments = buildReviewBookingAssignments(bookings)
  if (bookingAssignments.length && (documentInvoiceDetails.bookingTarget === 'document'
    || documentInvoiceDetails.reviewStatus !== 'complete')) {
    throw new Error('Document review payments require a complete accounting review')
  }

  return await updateFileInfo({
    id: fileId,
    tags: mergeReviewTags(tags, hostTags),
    documentInvoiceDetails,
    createExpenseFromReview,
    saveReviewPayee: true,
    reReviewSave,
    bookingAssignments,
  })
}
